// 他参加者が選択しているオブジェクトを、その参加者の色・アイコン・名前で表示するレイヤ。
// PO Board のエピックカード上プレゼンスと同じく、対象の端に小さな参加者バッジを重ねる。

import { useId, useMemo, type CSSProperties } from "react";
import type { CanvasObject, PenStrokeCanvasObject, Viewer } from "@refinement-canvas/domain";
import {
  getRemoteAvatarAllowedHttpsOrigins,
  isRenderableAvatarImageUrl,
} from "../../auth/avatar-image-url";
import { getSharedStrokePath } from "../../lib/pen-stroke";
import { PenStrokeSelectionOutline } from "./objects/pen-stroke-selection-outline";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import { useCanvasViewersStore } from "./store/canvas-viewers-store";
import { useRenderedObjectHeight } from "./use-rendered-object-height";

interface Props {
  readonly currentUserId: string;
}

interface RemoteSelection {
  readonly key: string;
  readonly object: CanvasObject;
  readonly viewer: Viewer;
  readonly overlapIndex: number;
}

const BADGE_HEIGHT_PX = 28;
// バッジと対象図形の上辺の隙間（画面 px）。
const BADGE_GAP_PX = 6;
// 同じ図形を複数人が選択したときの段積み間隔（画面 px）。
const BADGE_STACK_PX = BADGE_HEIGHT_PX + 4;

export function CanvasRemoteSelectionsLayer({ currentUserId }: Props): JSX.Element {
  const objects = useCanvasDocStore((s) => s.objects);
  const viewers = useCanvasViewersStore((s) => s.viewers);
  const selections = buildRemoteSelections(Object.values(viewers), objects, currentUserId);

  return (
    <div
      data-testid="canvas-remote-selections-layer"
      style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
    >
      {selections.map(({ key, object, viewer, overlapIndex }) => (
        <RemoteSelectionFrame
          key={key}
          object={object}
          overlapIndex={overlapIndex}
          viewer={viewer}
        />
      ))}
    </div>
  );
}

function buildRemoteSelections(
  viewers: readonly Viewer[],
  objects: Record<string, CanvasObject>,
  currentUserId: string
): readonly RemoteSelection[] {
  const overlapCounts = new Map<string, number>();
  const selections: RemoteSelection[] = [];

  for (const viewer of [...viewers].sort(compareViewer)) {
    if (viewer.userId === currentUserId) continue;
    const selectedObjectIds = viewer.selectedObjectIds ?? [];
    for (const objectId of selectedObjectIds) {
      const object = objects[objectId];
      if (!object) continue;
      const overlapIndex = overlapCounts.get(objectId) ?? 0;
      overlapCounts.set(objectId, overlapIndex + 1);
      selections.push({
        key: `${viewer.userId}:${objectId}`,
        object,
        viewer,
        overlapIndex,
      });
    }
  }

  return selections;
}

function RemoteSelectionFrame({
  object,
  overlapIndex,
  viewer,
}: {
  readonly object: CanvasObject;
  readonly overlapIndex: number;
  readonly viewer: Viewer;
}): JSX.Element {
  const color = normalizeViewerColor(viewer.color);
  // テキスト図形は本文量により実描画高さが object.h（minHeight 運用）を超え得る。object.h のままだと
  // 枠が本文より短くなり、末尾行の相手キャレットが枠の下へはみ出すため、自分の選択枠と同じく実測する。
  const renderedHeight = useRenderedObjectHeight(object.id, object.type === "text");
  const boxHeight =
    object.type === "text" && renderedHeight !== null
      ? Math.max(object.h, renderedHeight)
      : object.h;
  const containerStyle: CSSProperties = {
    position: "absolute",
    left: `${object.x}px`,
    top: `${object.y}px`,
    width: `${object.w}px`,
    height: `${boxHeight}px`,
    pointerEvents: "none",
    zIndex: Number.MAX_SAFE_INTEGER - 1,
  };
  const frameStyle: CSSProperties = {
    position: "absolute",
    inset: 0,
    // 枠線は拡大率に依らず画面上 2px に見せる（自分の選択アウトラインと同じ補正）。
    border: `calc(2px * var(--canvas-inv-zoom, 1)) solid ${color}`,
    // borderRadius と boxShadow は補正せずステージ px のまま残す。枠は図形の外接矩形そのもので
    // scale ラッパへ包めず、角丸は倍率が変わっても「角丸である」認識に影響せず、影は装飾のため。
    borderRadius: "8px",
    boxSizing: "border-box",
    transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
    transformOrigin: "center",
    boxShadow: `0 0 0 1px rgba(255, 255, 255, 0.9), 0 6px 16px ${withAlpha(color, 0.18)}`,
    pointerEvents: "none",
  };

  return (
    <div
      data-testid={`remote-selection-${viewer.userId}-${object.id}`}
      style={containerStyle}
    >
      {object.type === "pen-stroke" ? (
        // ペンストロークだけは外接矩形の枠を出さず、自分の選択表示と同じストローク形状の
        // 輪郭をその参加者の色で描く（共同編集中だけ四角い枠が現れるのを防ぐ）。
        <RemotePenStrokeSelectionOutline color={color} object={object} viewer={viewer} />
      ) : (
        <div
          aria-hidden="true"
          data-testid={`remote-selection-frame-${viewer.userId}-${object.id}`}
          style={frameStyle}
        />
      )}
      {/* 図形の上辺へ下端を張り付け、そこを起点に上方向へ画面固定サイズで伸ばす。
          図形との隙間と段積み間隔は scale の右側の translateY で表す（scale の右の関数は
          先に点へ適用されてから 1/zoom されるため、書いた px がそのまま画面上で効く）。
          margin は transform の外側で効き、画面固定にするには別の calc 係数が要るので採らない。 */}
      <div
        data-testid={`remote-selection-badge-scale-${viewer.userId}-${object.id}`}
        style={{
          position: "absolute",
          left: 0,
          bottom: "100%",
          transform: `scale(var(--canvas-inv-zoom, 1)) translateY(${-(
            BADGE_GAP_PX +
            overlapIndex * BADGE_STACK_PX
          )}px)`,
          transformOrigin: "left bottom",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            maxWidth: "240px",
            minHeight: `${BADGE_HEIGHT_PX}px`,
            padding: "3px 9px 3px 3px",
            border: `1px solid ${color}`,
            borderRadius: "9999px",
            background: "var(--color-surface)",
            boxShadow: "var(--shadow-1)",
            color: "var(--color-text)",
            fontSize: "12px",
            fontWeight: 700,
            whiteSpace: "nowrap",
          }}
        >
          <ViewerAvatar viewer={viewer} />
          <span
            style={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {viewer.displayName}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * 他参加者が選択中のペンストロークの輪郭。外接矩形と同じ位置・大きさの <svg> を容器へ重ね、
 * 自分の選択表示と同じ部品・同じアウトラインで、色だけをその参加者の色にして描く。
 */
function RemotePenStrokeSelectionOutline({
  color,
  object,
  viewer,
}: {
  readonly color: string;
  readonly object: PenStrokeCanvasObject;
  readonly viewer: Viewer;
}): JSX.Element {
  // 自分の選択表示（pen-stroke-object.tsx）と同じ関数でアウトラインを求め、幾何を一致させる。
  // 共有メモを通すため、同じストロークを複数の参加者が選択しても算出は 1 回で済む。
  const path = useMemo(() => getSharedStrokePath(object), [object]);
  // mask の id は mask="url(#...)" という CSS 構文の一部になるため、利用者由来の文字列を入れない。
  // userId（IdP の sub）と object.id はサーバスキーマが長さだけを制限しており、空白・")"・"#" などが
  // 入り得る。React の useId() は同一文書内で一意な値を返すため、そのまま連結して使う。
  // 戻り値から文字を削って整形してはいけない（React のバージョンごとに形式が異なり、
  // 削った結果が別の値と縮退しない保証が無い）。
  const maskId = `remote-pen-selection-mask-${useId()}`;
  return (
    <svg
      aria-hidden="true"
      data-testid={`remote-selection-outline-${viewer.userId}-${object.id}`}
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        // 絶対配置の置換要素（svg）は inset だけでは伸びないため、容器いっぱいの幅と高さを明示する。
        width: "100%",
        height: "100%",
        // 輪郭はマージン分だけ外接矩形の外へはみ出す。
        overflow: "visible",
        pointerEvents: "none",
        transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
        transformOrigin: "center",
      }}
      // points は stage 座標のまま保存されているため、自分用の svg と同じ viewBox で座標を一致させる。
      viewBox={`${object.x} ${object.y} ${object.w} ${object.h}`}
    >
      <PenStrokeSelectionOutline
        color={color}
        maskId={maskId}
        object={object}
        path={path}
        testId={`remote-selection-outline-path-${viewer.userId}-${object.id}`}
      />
    </svg>
  );
}

function ViewerAvatar({ viewer }: { readonly viewer: Viewer }): JSX.Element {
  const commonStyle: CSSProperties = {
    width: "22px",
    height: "22px",
    borderRadius: "9999px",
    flex: "0 0 auto",
  };

  // 他参加者が送ってくる avatarImageUrl は信頼できないため、data:image/... か信頼済み origin（IdP）
  // の https のみ描画する。任意 https による強制外部リクエスト/トラッキングを防ぐ。
  if (
    viewer.avatarImageUrl &&
    isRenderableAvatarImageUrl(viewer.avatarImageUrl, {
      allowedHttpsOrigins: getRemoteAvatarAllowedHttpsOrigins(),
    })
  ) {
    return (
      <img
        alt={`${viewer.displayName} のアイコン`}
        referrerPolicy="no-referrer"
        src={viewer.avatarImageUrl}
        style={{
          ...commonStyle,
          objectFit: "cover",
        }}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      style={{
        ...commonStyle,
        display: "inline-grid",
        placeItems: "center",
        background: normalizeViewerColor(viewer.color),
        color: "#ffffff",
        fontSize: "10px",
        lineHeight: 1,
      }}
    >
      {getFallbackLabel(viewer.displayName)}
    </span>
  );
}

function compareViewer(left: Viewer, right: Viewer): number {
  const byName = left.displayName.localeCompare(right.displayName, "ja");
  if (byName !== 0) return byName;
  return left.userId.localeCompare(right.userId, "ja");
}

function getFallbackLabel(displayName: string): string {
  const trimmed = displayName.trim().replace(/\s+/gu, "");
  if (trimmed.length === 0) return "?";
  return Array.from(trimmed).slice(0, 2).join("");
}

function normalizeViewerColor(color: string): string {
  return color.trim().length > 0 ? color : "#1e63d4";
}

function withAlpha(color: string, alpha: number): string {
  if (!/^#[0-9a-f]{6}$/iu.test(color)) return `rgba(30, 99, 212, ${alpha})`;
  const red = Number.parseInt(color.slice(1, 3), 16);
  const green = Number.parseInt(color.slice(3, 5), 16);
  const blue = Number.parseInt(color.slice(5, 7), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}
